import { useState, useEffect, useCallback } from "react";
import { useQuery, useLazyQuery } from "@apollo/client";
import useCapabilities, { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { useNavigate } from "@/router";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import usePageTitle from "@/hooks/usePageTitle";
import usePagination from "@/hooks/usePagination";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { ORDERS_QUERY, GET_TEMPLATES } from "./queries";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { OrderStage } from "../types";
import { Variables } from "./types";
import { Table as TableComponent } from "@tanstack/react-table";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { toFormat } from "@/lib/dateHelpers";
import { renderStatus } from "../_utils";
import { reject, isEmpty } from "lodash";
import TabLayout from "../_TabLayout";
import SearchInput from "@/components/SearchInput";
import CreateOrderModal from "./_components/CreateOrderModal";
import Button from "@/components/Button";
import Tooltip from "@/components/Tooltip";
import MultiSelect from "@/components/MultiSelect";
import Table, {
  TableControls,
  TableFooter,
  buildBulkSelectionColumn,
  buildExpandableCountMultiColumn,
  buildCopyTextColumn,
  buildActionColumn,
  type Row
} from "@/components/Table";
import type { Action } from "@/components/MenuButton";
import { useParams } from "@/hooks/useSearchParams";
import { OrderRow, OrderNode } from "../types";
import { Path } from "@/router";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { buildColumns } from "@/pages/billing/_utils";
import { ProductTable } from "@/features/billing/components/ProductTable";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import FacilityAutocomplete from "../_components/FacilityAutocomplete";
import DateSelect from "@/components/DateSelect";
import { DateTime } from "luxon";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import NotesTable from "@/features/global/components/NotesTable";
import { NoteNotableValues } from "@/types";
import { getCopyPaymentUrlAction } from "./copyPaymentUrlAction";

interface TemplateData {
  id: string;
  name: string;
  type: string;
  lookupKey: string;
}

const mapCommerceOrders = (data: OrderNode[]) =>
  (data || []).map(({ updatedAt, dueDate, balance, lineItems, notes, ...order }) => ({
    ...order,
    pgaId: order.person.pgaId,
    updatedAt: formatDate(updatedAt),
    dueDate: formatDate(dueDate),
    balance: centsToDollarsFormatted(balance) || undefined,
    products: lineItems.length,
    lineItems: lineItems,
    notes: notes || []
  }));

const formatDate = (date?: string): string | undefined => (date && toFormat(date)) || undefined;

export default function OrdersPage() {
  usePageTitle("/billing/orders", "Billing | Orders");
  useRequireCapability({ domain: DOMAIN.COMMERCE, permission: PERMISSION.viewOrders });
  const { params, addSearchParam, deleteSearchParams, searchParamsUrl } = useParams();
  if (params.datetimeRange) {
    params.datetimeRange = params.datetimeRange && {
      from: params.datetimeRange.split(",")[0],
      to: params.datetimeRange.split(",")[1]
    };
  }
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { rowSelection, setRowSelection, bulkSelectionProps, isSelectedOnly, deselectAll, rowSelectionCount } =
    useBulkSelection();
  const { Menu, setMenu } = useTableRowMenu();
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState<boolean>(false);
  const [selectedData, setSelectedData] = useState<OrderRow[]>([]);
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | undefined>(undefined);
  const { hasPermission } = useCapabilities();
  const [selectedStatusValues, setSelectedStatusValues] = useState<string[]>(params.statuses || []);
  const [selectedTemplateValues, setSelectedTemplateValues] = useState<string[]>(params.templates || []);
  const [expanded, setExpanded] = useState<Record<string, string | null>>({});
  const onExpand = useCallback((rowId: string, panelId: string | null) => {
    setExpanded((prev) => ({ ...prev, [rowId]: panelId }));
  }, []);
  const { isCompactLayout } = useIsCompactLayout();
  const form = useForm();
  const { setValue, watch, reset, control } = form;
  const facilityIds = watch("facilityIds");
  const [variables, setVariables] = useState<Variables>(params);
  const { data, loading } = useQuery(ORDERS_QUERY, { variables: { ...variables, ...paginationVariables } });
  const [selectedOrdersQuery, { data: allSelectedData, loading: allSelectedLoading }] = useLazyQuery(ORDERS_QUERY);
  const { data: templatesData } = useQuery<{ commerceOrderTemplates: TemplateData[] }>(GET_TEMPLATES);

  const navigate = useNavigate();
  const pattern = "/billing/orders";

  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "query",
    debounceMs: 300
  });
  const orderData: OrderRow[] = mapCommerceOrders(data?.commerceOrders.nodes || []);
  const allSelectedDataArray: OrderRow[] = mapCommerceOrders(allSelectedData?.commerceOrders.nodes || []);

  const isNotPayable = selectedData.some((item) => !item.payable);

  function convertToUrlParams(rowSelection: Record<string, boolean>) {
    return Object.keys(rowSelection)
      .map((item) => `orderId=${item}`)
      .join("&");
  }
  const selectableRows = orderData.filter((row) => row.payable || row.status === OrderStage.DRAFT);
  useEffect(() => {
    if (isSelectedOnly) {
      const selectedIds = Object.keys(rowSelection).filter(Boolean);
      if (selectedIds.length > 0) {
        selectedOrdersQuery({ variables: { ids: selectedIds } });
      }
    }
  }, [isSelectedOnly, rowSelection, selectedOrdersQuery]);

  useEffect(() => {
    if (loading && params.facilityIds) {
      setValue("facilityIds", params.facilityIds);
    }
  }, [loading, params, setValue]);

  const filterState = {
    status: selectedStatusValues.length > 0,
    templates: selectedTemplateValues.length > 0,
    facility: !!facilityIds?.length,
    dates: !!variables.datetimeRange?.from && !!variables.datetimeRange?.to
  };

  const activeFilterCount = Object.values(filterState).filter(Boolean).length;

  const handleClearAllFilters = () => {
    setVariables((prevValue) => ({
      query: prevValue.query
    }));
    clearSearch();
    reset({ dates: { startDate: null, endDate: null } as any });
    setValue("facilityIds", []);
    setValue("facility", "");
    setSelectedStatusValues([]);
    setSelectedTemplateValues([]);
    resetPagination();
    deleteSearchParams(["statuses[]", "templates[]", "facilityIds[]", "facility", "datetimeRange"]);
  };

  useEffect(() => {
    setVariables((prevValue: Variables) => {
      const { query, ...restVariables } = prevValue;
      return {
        ...restVariables,
        ...(debouncedSearch && { query: debouncedSearch }),
        ...(selectedTemplateValues.length > 0 && { templates: selectedTemplateValues }),
        ...(facilityIds ? { facilityId: facilityIds[0] } : {})
      };
    });
  }, [debouncedSearch, selectedTemplateValues, facilityIds, setVariables]);

  const getRowClasses = (_row: Row<OrderRow>) => {
    return "";
  };

  const renderNotesPanel = useCallback(
    (row: { original: OrderRow }) => {
      const notes = row.original.notes || [];
      if (notes.length === 0) return null;

      return (
        <NotesTable
          variant="details-panel"
          data={notes}
          title={`Notes (${notes.length})`}
          noteEditing={{
            enabled: hasPermission(PERMISSION.manageOrders),
            notableId: row.original.id,
            notableType: NoteNotableValues.ORDER,
            refetchQueries: [ORDERS_QUERY]
          }}
        />
      );
    },
    [hasPermission]
  );

  const bulkActions = hasPermission(PERMISSION.managePayments)
    ? [
        {
          label: "Apply Payment",
          icon: "payments" as const,
          variant: "outlined" as const,
          disabled: isNotPayable,
          onClick: () => {
            navigate(`/billing/orders/apply-payment?${convertToUrlParams(rowSelection)}` as Path);
          },
          render: isNotPayable
            ? () => (
                <Tooltip supportingText="Select only open orders to apply payment" variant="plain" color="black">
                  <Button
                    variant="outlined"
                    disabled={true}
                    onClick={() => {
                      navigate(`/billing/orders/apply-payment?${convertToUrlParams(rowSelection)}` as Path);
                    }}
                  >
                    Apply Payment
                  </Button>
                </Tooltip>
              )
            : undefined
        }
      ]
    : [];

  const pageActions: Action[] = hasPermission(PERMISSION.manageOrders)
    ? [
        {
          text: "Create Order",
          iconName: "add",
          variant: "filled",
          onClick: () => setIsCreateOrderModalOpen(true)
        }
      ]
    : [];

  return (
    <TabLayout>
      <div className="flex w-full">
        <div className="w-full">
          <div className="pt-12" />
          <div className="w-full">
            <Table
              data={(isSelectedOnly ? allSelectedDataArray : orderData) || []}
              loading={loading || allSelectedLoading}
              enableRowSelection={(row) => row.original.payable || row.original.status === OrderStage.DRAFT}
              onClickRow={(row, event) => {
                // Cmd+click (Mac) or Ctrl+click (Windows/Linux) opens in new tab
                if (event?.metaKey || event?.ctrlKey) {
                  window.open(`${pattern}/${row.original.id}?return_to=/billing/orders&${searchParamsUrl}`, "_blank");
                } else {
                  // Regular click navigates to order details page
                  navigate(`${pattern}/${row.original.id}?return_to=/billing/orders&${searchParamsUrl}` as Path);
                }
              }}
              getRowClasses={getRowClasses}
              getRowCanExpand={(row) =>
                (row.original.lineItems?.length ?? 0) > 0 || (row.original.notes?.length ?? 0) > 0
              }
              renderExpandedRow={({ row }) => {
                if (isCompactLayout) {
                  return (
                    <>
                      <ProductTable row={row} />
                      {renderNotesPanel(row)}
                    </>
                  );
                }
                switch (expanded[row.id]) {
                  case "products":
                    return <ProductTable row={row} />;
                  case "notes":
                    return renderNotesPanel(row) ?? <></>;
                  default:
                    return <></>;
                }
              }}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              setSelectedData={setSelectedData}
              hasActiveFilters={activeFilterCount > 0 || !!(searchValue || "").trim()}
              renderControls={(table: TableComponent<OrderRow>) => (
                <FormProvider {...form}>
                  <TableControls
                    pagination={createHeaderPagination(data?.commerceOrders, {
                      totalCount: data?.commerceOrders.totalCount || 0,
                      pageCount: orderData.length
                    })}
                    actions={pageActions}
                    useExpandedFilters={true}
                    expandedFiltersState={filterState}
                    onClearAllFilters={handleClearAllFilters}
                    activeFilters={activeFilterCount}
                    bulkActions={bulkActions}
                    {...bulkSelectionProps}
                    onDeselectAll={() => {
                      deselectAll();
                      resetPagination();
                    }}
                    totalRowCount={rowSelectionCount}
                    onToggleAllRows={(checked) => table.toggleAllRowsSelected(checked)}
                    allRowsSelected={table.getIsAllRowsSelected()}
                    someRowsSelected={table.getIsSomeRowsSelected()}
                  >
                    <SearchInput
                      placeholder="Search Orders"
                      query={searchValue}
                      onClear={() => {
                        clearSearch();
                        resetPagination();
                      }}
                      onChange={(e) => {
                        setSearchValue(e);
                        resetPagination();
                      }}
                    />
                    <div data-name="status">
                      <Controller
                        name="status"
                        control={control}
                        render={() => (
                          <MultiSelect
                            label="Stage"
                            selectedValues={selectedStatusValues}
                            options={[
                              { value: "canceled", label: "Canceled" },
                              { value: "draft", label: "Draft" },
                              { value: "open", label: "Open" },
                              { value: "paid", label: "Paid" },
                              { value: "pending", label: "Pending" },
                              { value: "uncollectible", label: "Uncollectible" }
                            ]}
                            onSelect={(value, checked) => {
                              const status = checked
                                ? [...selectedStatusValues, value]
                                : selectedStatusValues.filter((v) => v !== value);

                              setSelectedStatusValues(reject(status, isEmpty));
                              setVariables((prevValue) => ({
                                ...prevValue,
                                statuses: reject(status, isEmpty)
                              }));
                              resetPagination();
                              addSearchParam("statuses[]", reject(status, isEmpty).join(","));
                            }}
                            onClear={() => {
                              setSelectedStatusValues([]);
                              setVariables((prevValue) => ({
                                ...prevValue,
                                statuses: []
                              }));
                              deleteSearchParams("statuses[]");
                            }}
                            className="w-full"
                          />
                        )}
                      />
                    </div>
                    <div data-name="templates" data-filter-size="custom">
                      <Controller
                        name="templates"
                        control={control}
                        render={() => (
                          <MultiSelect
                            label="Template"
                            selectedValues={selectedTemplateValues}
                            options={
                              templatesData?.commerceOrderTemplates.map((template: TemplateData) => ({
                                value: template.id,
                                label: template.name
                              })) || []
                            }
                            onSelect={(value, checked) => {
                              const templates = checked
                                ? [...selectedTemplateValues, value]
                                : selectedTemplateValues.filter((v) => v !== value);

                              setSelectedTemplateValues(reject(templates, isEmpty));
                              setVariables((prevValue) => ({
                                ...prevValue,
                                templates: reject(templates, isEmpty)
                              }));
                              resetPagination();
                              addSearchParam("templates[]", reject(templates, isEmpty).join(","));
                            }}
                            onClear={() => {
                              setSelectedTemplateValues([]);
                              setVariables((prevValue) => ({
                                ...prevValue,
                                templates: []
                              }));
                              deleteSearchParams("templates[]");
                            }}
                            className="w-full md:min-w-[20rem]"
                          />
                        )}
                      />
                    </div>
                    <div data-name="facility">
                      <FacilityAutocomplete
                        params={params}
                        addSearchParam={addSearchParam}
                        deleteSearchParams={deleteSearchParams}
                        name="facility"
                      />
                    </div>
                    <div data-name="dates" data-filter-size="custom">
                      <Controller
                        name="dates"
                        control={control}
                        render={({ field }) => (
                          <DateSelect
                            label="Created Date Range"
                            placeholder="Show All"
                            popoverDirection="down"
                            useDefaultShortcuts={true}
                            useModal={true}
                            value={
                              params.datetimeRange
                                ? {
                                    startDate: params.datetimeRange.from.split("T")[0],
                                    endDate: params.datetimeRange.to.split("T")[0]
                                  }
                                : field.value
                            }
                            onChange={(e) => {
                              field.onChange(e);
                              if (typeof e === "object" && e !== null) {
                                const startDate =
                                  e.startDate instanceof Date
                                    ? e.startDate.toISOString().split("T")[0]
                                    : DateTime.fromISO(e?.startDate?.toString() || "", { zone: "utc" })
                                        .startOf("day")
                                        .toISO()!;

                                const endDate =
                                  e.endDate instanceof Date
                                    ? e.endDate.toISOString().split("T")[0]
                                    : DateTime.fromISO(e?.endDate?.toString() || "", { zone: "utc" })
                                        .endOf("day")
                                        .toISO()!;

                                setVariables((prevValue) => ({
                                  ...prevValue,
                                  datetimeRange: { from: startDate, to: endDate }
                                }));
                                if (startDate && endDate) {
                                  addSearchParam("datetimeRange", [startDate, endDate].join(","));
                                } else {
                                  deleteSearchParams("datetimeRange");
                                }
                              }
                            }}
                          />
                        )}
                      />
                    </div>
                  </TableControls>
                </FormProvider>
              )}
              columns={[
                buildBulkSelectionColumn(),
                ...buildColumns({
                  isCompactLayout,
                  hasCheckbox: false,
                  selectableRows,
                  getIsSomeRowsSelected: undefined,
                  setIsSelectedOnly: undefined
                }),
                buildCopyTextColumn<OrderRow>({
                  accessorKey: "pgaId",
                  header: "PGA ID",
                  copyFieldLabel: "PGA ID",
                  getText: (row) => row.pgaId,
                  enableSorting: false
                }),
                {
                  accessorKey: "description",
                  header: "Description",
                  enableSorting: false,
                  meta: { headerClassName: "hide-header-in-column" },
                  size: 280,
                  minSize: 200
                },
                buildExpandableCountMultiColumn<OrderRow>({
                  panelId: "products",
                  accessorKey: "products",
                  header: "Products",
                  getCount: (row) => row.lineItems?.length ?? 0,
                  expanded,
                  onExpand
                }),
                {
                  accessorKey: "updatedAt",
                  header: "Modified",
                  enableSorting: false
                },
                buildExpandableCountMultiColumn<OrderRow>({
                  panelId: "notes",
                  accessorKey: "notes",
                  header: "Notes",
                  getCount: (row) => row.notes?.length ?? 0,
                  expanded,
                  onExpand
                }),
                {
                  accessorKey: "dueDate",
                  header: "Due Date",
                  enableSorting: false
                },
                {
                  accessorKey: "balance",
                  header: "Balance",
                  enableSorting: false
                },
                {
                  header: "Stage",
                  id: "stage",
                  cell: ({ row: { original } }) => renderStatus(original.status, original.paymentStatus),
                  size: 100,
                  meta: {
                    headerClassName: "hide-header-in-column"
                  }
                },
                buildActionColumn<OrderRow>({
                  getActions: (row) => {
                    const actions: Action[] = [];

                    if (hasPermission(PERMISSION.managePayments) && (row.payable || row.status === OrderStage.DRAFT)) {
                      actions.push({
                        text: "Apply Payment",
                        onClick: () => {
                          navigate(`/billing/orders/apply-payment?orderId=${row.id}` as Path);
                        }
                      });
                    }

                    const copyPaymentUrlAction = getCopyPaymentUrlAction({
                      id: row.id,
                      type: row.type,
                      payable: !!row.payable
                    });
                    if (copyPaymentUrlAction) {
                      actions.push(copyPaymentUrlAction);
                    }

                    actions.push({
                      text: "View Order",
                      onClick: () => {
                        navigate(`${pattern}/${row.id}?return_to=/billing/orders&${searchParamsUrl}` as Path);
                      }
                    });

                    return actions;
                  },
                  onViewClick: (row) => {
                    setSelectedPgaId(row.person?.pgaId);
                    setIsPersonSideSheetOpen(true);
                  },
                  viewButtonTitle: "View person details",
                  actionId,
                  setMenu
                })
              ]}
              renderFooter={() => (
                <TableFooter
                  pagination={createFooterPagination(data?.commerceOrders, {
                    totalCount: data?.commerceOrders.totalCount || 0,
                    pageCount: orderData.length
                  })}
                  totalRowCount={data?.commerceOrders?.totalCount}
                  {...bulkSelectionProps}
                />
              )}
            />
          </div>
        </div>
        {isCreateOrderModalOpen && <CreateOrderModal onDismiss={() => setIsCreateOrderModalOpen(false)} />}
      </div>

      <Menu />
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(undefined);
        }}
        pgaId={selectedPgaId ?? null}
      />
    </TabLayout>
  );
}
